import { useState, useCallback, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from './useAuth';
import { Client } from '../types';
import { clientCascadeDelete } from '../lib/clientCascadeDelete';

const RAG_ENDPOINT = process.env.NEXT_PUBLIC_RAG_ENDPOINT || '';
const RAG_BEARER = process.env.RAG_SERVICE_BEARER_TOKEN || '';
const RAG_MODEL_VERSION = process.env.NEXT_PUBLIC_RAG_MODEL_VERSION || 'gemini-2.5-flash-001';
const RAG_REQUIRE_HOOK = (process.env.NEXT_PUBLIC_RAG_HOOK_ENABLED || 'true').toLowerCase() === 'true';

async function triggerExtraction(opts: {
  clientId: string;
  filePath: string;
  bucket: string;
  size: number;
  hash: string;
  docId: string;
  mode?: string;
  mime?: string;
}) {
  if (!RAG_REQUIRE_HOOK) return;
  const endpoint = (RAG_ENDPOINT || '').replace(/\/$/, '');
  if (!endpoint || !RAG_BEARER) {
    console.warn('[rag-hook] skipped: missing endpoint or bearer');
    return;
  }
  const payload = {
    docId: opts.docId,
    clientId: opts.clientId,
    bucket: opts.bucket,
    filePath: opts.filePath,
    fingerprint: {
      hash: opts.hash,
      size: opts.size,
      path: opts.filePath,
    },
    modelVersion: RAG_MODEL_VERSION,
    mode: opts.mode || 'general',
    mime: opts.mime,
  };
  try {
    const res = await fetch(`${endpoint}/rag/extract`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${RAG_BEARER}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.warn('[rag-hook] extract failed', res.status, text);
    } else {
      console.log('[rag-hook] extract triggered', payload.docId);
    }
  } catch (err) {
    console.warn('[rag-hook] extract error', (err as any)?.message || err);
  }
}

async function computeSha256(buffer: ArrayBuffer): Promise<string> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.warn('[rag-hook] sha256 failed', (err as any)?.message || err);
    return '';
  }
}

function buildRagDocId(clientId: string, filePath: string, hash: string, size?: number): string {
  const pathKey = Array.from(`${clientId}:${filePath}`).reduce((acc, ch) => ((acc * 31 + ch.charCodeAt(0)) >>> 0), 0);
  const safeHash = (hash || 'doc').replace(/[^a-z0-9]/gi, '').slice(0, 16) || 'doc';
  const sizePart = typeof size === 'number' && Number.isFinite(size) ? Math.max(0, Math.floor(size)).toString(16) : '0';
  return `rag_${safeHash}_${pathKey.toString(16)}_${sizePart}`;
}
/**
 * Custom hook for managing clients in Firestore
 * @returns Object with client data and management functions
 */
export const useClientManagement = () => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch all clients for the current user
  const fetchClients = useCallback(async () => {
    if (!currentUser?.uid) {
      console.log('No user ID available, clearing clients list');
      setClients([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching clients for user:', currentUser.uid);
      
      // Check if token is available and not expired
      if (currentUser.stsTokenManager) {
        const expirationTime = currentUser.stsTokenManager.expirationTime;
        const now = Date.now();
        // Add a buffer of 5 minutes to prevent edge cases
        if (expirationTime < now + (5 * 60 * 1000)) {
          console.warn('Token expired or expiring soon. Need to re-authenticate.');
          setError(new Error('Your session has expired. Please sign in again.'));
          setClients([]);
          setLoading(false);
          return;
        }
      }
      
      const clientsQuery = query(
        collection(db, 'clients'),
        where('createdBy', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(clientsQuery);
      console.log('Client query completed, found:', querySnapshot.size, 'documents');
      
      const clientsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Client));
      
      setClients(clientsData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      
      // Check for specific Firebase auth errors
      if (err.code === 'permission-denied' || 
          err.code?.includes('auth') || 
          err.message?.includes('auth') ||
          err.message?.includes('permission')) {
        setError(new Error('Authentication error. Please sign in again.'));
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      
      // Important: clear clients array so UI doesn't stay in loading state
      setClients([]);
    } finally {
      // Ensure loading is set to false in all cases
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Fetch a specific client by ID
  const fetchClient = useCallback(async (clientId: string) => {
    if (!currentUser?.uid || !clientId) return null;
    
    setLoading(true);
    try {
      const clientDoc = await getDoc(doc(db, 'clients', clientId));
      
      if (clientDoc.exists()) {
        const clientData = { 
          id: clientDoc.id, 
          ...clientDoc.data() 
        } as Client;
        
        setCurrentClient(clientData);
        setError(null);
        return clientData;
      } else {
        setCurrentClient(null);
        return null;
      }
    } catch (err) {
      console.error('Error fetching client:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Create a new client
  const createClient = useCallback(async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
    if (!currentUser?.uid) return null;
    
    setLoading(true);
    try {
      const newClientRef = doc(collection(db, 'clients'));
      const timestamp = new Date().toISOString();
      
      const newClient: Client = {
        ...clientData,
        id: newClientRef.id,
        createdAt: timestamp,
        updatedAt: timestamp,
        createdBy: currentUser.uid
      };
      
      await setDoc(newClientRef, newClient);
      setError(null);
      
      // Update the local state
      setClients(prev => [...prev, newClient]);
      setCurrentClient(newClient);
      
      return newClient;
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Update an existing client
  const updateClient = useCallback(async (clientId: string, updates: Partial<Client>) => {
    if (!currentUser?.uid) return false;
    
    setLoading(true);
    try {
      const clientRef = doc(db, 'clients', clientId);
      // Remove undefined values (Firestore rejects undefined)
      const deepStripUndefined = (obj: any): any => {
        if (Array.isArray(obj)) return obj.map(deepStripUndefined).filter(v => v !== undefined);
        if (obj && typeof obj === 'object') {
          const out: any = {};
          for (const [k, v] of Object.entries(obj)) {
            const cleaned = deepStripUndefined(v);
            if (cleaned !== undefined) out[k] = cleaned;
          }
          return out;
        }
        return obj === undefined ? undefined : obj;
      };

      const sanitized = deepStripUndefined(updates);
      // Add updated timestamp
      const updatedData = {
        ...sanitized,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(clientRef, updatedData);
      
      // Update local state
      setClients(prev => 
        prev.map(client => 
          client.id === clientId
            ? { ...client, ...updatedData }
            : client
        )
      );
      
      if (currentClient?.id === clientId) {
        setCurrentClient(prev => prev ? { ...prev, ...updatedData } : null);
      }
      
      setError(null);
      return true;
    } catch (err) {
      console.error('Error updating client:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Delete a client and all related data
  const deleteClient = useCallback(async (clientId: string) => {
    if (!currentUser?.uid) return false;
    
    setLoading(true);
    try {
      // Verify ownership before deletion
      const hasOwnership = await clientCascadeDelete.verifyClientOwnership(clientId, currentUser.uid);
      if (!hasOwnership) {
        throw new Error('You do not have permission to delete this client');
      }

      // Perform cascade deletion
      const result = await clientCascadeDelete.cascadeDeleteClient(clientId, currentUser.uid);
      
      if (!result.success) {
        const errorMessage = result.errors.length > 0 
          ? result.errors.join('; ') 
          : 'Failed to delete client and related data';
        throw new Error(errorMessage);
      }

      // Log deletion summary for debugging
      console.log('Client deletion completed:', {
        clientId,
        deletedItems: result.deletedItems,
        totalErrors: result.errors.length
      });
      
      // Update local state only if deletion was successful
      setClients(prev => prev.filter(client => client.id !== clientId));
      
      if (currentClient?.id === clientId) {
        setCurrentClient(null);
      }
      
      setError(null);
      return true;
    } catch (err) {
      console.error('Error deleting client:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Get preview of what will be deleted (for confirmation dialogs)
  const getDeletePreview = useCallback(async (clientId: string) => {
    if (!currentUser?.uid) return null;
    
    try {
      return await clientCascadeDelete.getDeletePreview(clientId, currentUser.uid);
    } catch (err) {
      console.error('Error getting delete preview:', err);
      return null;
    }
  }, [currentUser?.uid]);

  // Upload client logo
  const uploadClientLogo = useCallback(async (clientId: string, file: File) => {
    if (!currentUser?.uid || !clientId) return null;
    
    try {
      const logoRef = ref(storage, `clients/${clientId}/logo/${file.name}`);
      await uploadBytes(logoRef, file);
      const logoUrl = await getDownloadURL(logoRef);
      
      // Update client with logo URL
      await updateClient(clientId, { logo: logoUrl });
      
      return logoUrl;
    } catch (err) {
      console.error('Error uploading client logo:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [currentUser?.uid, updateClient]);

  // Upload client document
  const uploadClientDocument = useCallback(async (
    clientId: string, 
    file: File,
    documentData: {
      title: string,
      type: 'press_release' | 'media_kit' | 'fact_sheet' | 'crisis_plan' | 'other'
    }
  ) => {
    if (!currentUser?.uid || !clientId) return null;
    
    try {
      const storagePath = `clients/${clientId}/documents/${file.name}`;
      const docRef = ref(storage, storagePath);
      const uploaded = await uploadBytes(docRef, file);
      const docUrl = await getDownloadURL(docRef);
      const arrayBuffer = await file.arrayBuffer();
      const hash = await computeSha256(arrayBuffer);
      const docId = buildRagDocId(clientId, storagePath, hash, uploaded.metadata.size || file.size);
      const uploadedAt = new Date().toISOString();
      
      // Create document record
      const newDocument = {
        id: docId,
        ragDocId: docId,
        title: documentData.title,
        type: documentData.type,
        url: docUrl,
        uploadedAt,
        status: 'queued' as const,
        modelVersion: RAG_MODEL_VERSION,
      };
      
      // Update client with new document
      const client = await fetchClient(clientId);
      if (client) {
        // Guard against clients that don't yet have a context object
        const context = client.context || {};
        const updatedDocuments = [
          ...(context.relevantDocuments || []).filter((doc: any) => doc.id !== docId && doc.ragDocId !== docId),
          newDocument
        ];

        await updateClient(clientId, {
          context: {
            ...context,
            relevantDocuments: updatedDocuments
          }
        });
      }
      
      // Trigger RAG extraction server-side
      await triggerExtraction({
        clientId,
        docId,
        filePath: storagePath,
        bucket: uploaded.metadata.bucket,
        size: uploaded.metadata.size || file.size,
        hash,
        mode: documentData.type === 'crisis_plan' ? 'legal' : 'general',
        mime: uploaded.metadata.contentType || file.type,
      });
      
      return newDocument;
    } catch (err) {
      console.error('Error uploading client document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [currentUser?.uid, fetchClient, updateClient]);

  // Update client document metadata (title, type) and optionally replace file
  const updateClientDocument = useCallback(async (
    clientId: string,
    documentId: string,
    updates: { title?: string; type?: string },
    newFile?: File,
    oldFileUrl?: string
  ) => {
    if (!currentUser?.uid || !clientId) return false;

    try {
      const client = await fetchClient(clientId);
      if (!client?.context?.relevantDocuments) return false;

      let newUrl: string | undefined;
      let newDocId: string | undefined;
      let newStoragePath: string | undefined;
      let newHash: string | undefined;
      let uploadedMeta: any;

      // If replacing file, upload new one and delete old one
      if (newFile) {
        // Upload new file
        newStoragePath = `clients/${clientId}/documents/${newFile.name}`;
        const docRef = ref(storage, newStoragePath);
        uploadedMeta = await uploadBytes(docRef, newFile);
        newUrl = await getDownloadURL(docRef);
        const buffer = await newFile.arrayBuffer();
        newHash = await computeSha256(buffer);
        newDocId = buildRagDocId(clientId, newStoragePath, newHash, uploadedMeta.metadata.size || newFile.size);

        // Delete old file if URL provided and different from new
        if (oldFileUrl && oldFileUrl !== newUrl) {
          try {
            const oldRef = ref(storage, oldFileUrl);
            await deleteObject(oldRef);
          } catch (storageErr: any) {
            if (storageErr.code !== 'storage/object-not-found') {
              console.warn('Old file delete warning:', storageErr);
            }
          }
        }
      }

      const updatedDocuments = client.context.relevantDocuments.map((doc: any) => {
        if (doc.id !== documentId) return doc;
        return {
          ...doc,
          ...updates,
          ...(newUrl ? { url: newUrl } : {}),
          ...(newDocId ? { id: newDocId, ragDocId: newDocId, status: 'queued', modelVersion: RAG_MODEL_VERSION } : {}),
          ...(newDocId ? { uploadedAt: new Date().toISOString() } : {}),
        };
      });

      await updateClient(clientId, {
        context: {
          ...client.context,
          relevantDocuments: updatedDocuments
        }
      });

      if (newDocId && newStoragePath && newHash) {
        await triggerExtraction({
          clientId,
          docId: newDocId,
          filePath: newStoragePath,
          bucket: uploadedMeta?.metadata?.bucket || '',
          size: uploadedMeta?.metadata?.size || newFile?.size || 0,
          hash: newHash,
          mode: updates.type === 'crisis_plan' ? 'legal' : 'general',
          mime: uploadedMeta?.metadata?.contentType || newFile?.type,
        });
      }

      return true;
    } catch (err) {
      console.error('Error updating client document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }, [currentUser?.uid, fetchClient, updateClient]);

  // Delete client document
  const deleteClientDocument = useCallback(async (
    clientId: string,
    documentId: string,
    storageUrl?: string
  ) => {
    if (!currentUser?.uid || !clientId) return false;

    try {
      // Delete from storage if URL provided
      if (storageUrl) {
        try {
          const storageRef = ref(storage, storageUrl);
          await deleteObject(storageRef);
        } catch (storageErr: any) {
          // Continue even if storage delete fails (file might not exist)
          if (storageErr.code !== 'storage/object-not-found') {
            console.warn('Storage delete warning:', storageErr);
          }
        }
      }

      // Delete from RAG/Supabase (don't block on failure - graceful degradation)
      if (documentId) {
        try {
          const ragResp = await fetch('/api/rag/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ docId: documentId, clientId }),
          });
          if (!ragResp.ok) {
            const errData = await ragResp.json().catch(() => ({}));
            console.warn('[rag-delete] failed:', ragResp.status, errData?.error || '');
          } else {
            console.log('[rag-delete] success:', documentId);
          }
        } catch (ragErr: any) {
          console.warn('[rag-delete] error:', ragErr?.message || ragErr);
        }
      }

      // Remove from client document list
      const client = await fetchClient(clientId);
      if (!client?.context?.relevantDocuments) return false;

      const updatedDocuments = client.context.relevantDocuments.filter(
        (doc: any) => doc.id !== documentId
      );

      await updateClient(clientId, {
        context: {
          ...client.context,
          relevantDocuments: updatedDocuments
        }
      });

      return true;
    } catch (err) {
      console.error('Error deleting client document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }, [currentUser?.uid, fetchClient, updateClient]);

  // Load clients when the hook is initialized - with proper dependency control
  useEffect(() => {
    // Use a module-level variable to track initialization across renders
    // This ensures we only fetch once per mount, not on every render
    let isMounted = true;
    
    const initializeClients = async () => {
      if (!isMounted) return;
      
      try {
        // Only fetch if we have clients array empty - prevents refetching
        if (clients.length === 0 && currentUser?.uid) {
          console.log('Initializing clients - first load');
          await fetchClients();
        }
      } catch (err) {
        console.error("Error initializing clients:", err);
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };
    
    if (currentUser?.uid) {
      initializeClients();
    } else {
      setLoading(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]); // Remove fetchClients from dependencies - crucial fix

  return {
    clients,
    currentClient,
    loading,
    error,
    fetchClients,
    fetchClient,
    createClient,
    updateClient,
    deleteClient,
    getDeletePreview,
    uploadClientLogo,
    uploadClientDocument,
    updateClientDocument,
    deleteClientDocument,
    setCurrentClient
  };
};
